import { useEffect, useMemo, useState } from 'react';
import { Building2, ChevronDown, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { ShareSheet } from '@/components/common/ShareSheet';
import { useAuth } from '@/contexts/AuthContext';
import { listAidProviders } from '@/api/aid';
import {
  buildWildfireSharePayload,
  wildfireStateLabel,
  type ShareableWildfire,
} from '@/utils/wildfireShare';
import { cn } from '@/lib/utils';

/** Trimmed from the `/aid/providers` row — the picker only needs the label. */
interface AidProvider {
  key: string;
  kind: 'fpa' | 'org';
  name: string;
  org_type?: string | null;
}

const ORG_TYPE_LABELS: Record<string, string> = {
  fire_department: 'Fire department',
  private_service: 'Private service',
  government: 'Government',
  ngo: 'NGO',
  other: 'Other',
};

// One fetch of the directory per tenant per session. It is a small, slow-moving
// register and the sheet is opened repeatedly from the map.
const providerCache = new Map<string, Promise<AidProvider[]>>();

function loadAidProviders(tenantId: number | null | undefined): Promise<AidProvider[]> {
  const key = String(tenantId ?? 'own');
  const cached = providerCache.get(key);
  if (cached) return cached;
  const promise = (
    listAidProviders(tenantId ?? undefined) as Promise<{ providers?: AidProvider[] }>
  )
    .then((data) => data?.providers ?? [])
    .catch((err) => {
      console.warn('Failed to load aid providers:', err);
      providerCache.delete(key); // let a later open retry
      return [] as AidProvider[];
    });
  providerCache.set(key, promise);
  return promise;
}

interface WildfireShareSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while the caller has nothing selected — the sheet stays closed. */
  wildfire: ShareableWildfire | null;
  /**
   * The FIRE's FPA, for the mutual-aid directory — that FPA is left out of
   * its own options. Pass the fire's tenant, not the signed-in user's: a
   * super admin has no own FPA, and keying off the caller would offer
   * Roodeplaat as its own assistant on a Roodeplaat fire. Same trap
   * `MutualAidPicker` documents. Omitted, the backend pins a normal user to
   * their own FPA anyway.
   */
  tenantId?: number | null;
  /** The caller's own date formatting, so the message reads like the screen. */
  formatDateTime?: (value?: string | null) => string;
}

/**
 * The share sheet for a wildfire, on every surface that sends one — the map
 * drawer and the report page.
 *
 * It exists so the "who is responding" controls and the payload they feed
 * live in one place. Those controls show only on an ACTIVE fire, and only to
 * a signed-in user: saying resources are on the way to a fire that is out is
 * not something anyone means to send, and the public map's sharer is a member
 * of the public with nothing to declare (and no access to the aid directory).
 *
 * The state is per-message, not per-fire. It resets whenever the sheet closes
 * or a different fire is selected — a stale selection carried over from the
 * last share would put a false claim about who is responding into the next.
 */
export function WildfireShareSheet({
  open,
  onOpenChange,
  wildfire,
  tenantId,
  formatDateTime,
}: WildfireShareSheetProps) {
  const { isAuthenticated } = useAuth();
  const [fireDepartmentEnroute, setFireDepartmentEnroute] = useState(false);
  const [fpaResourcesEnroute, setFpaResourcesEnroute] = useState(false);
  const [aidKeys, setAidKeys] = useState<string[]>([]);
  const [providers, setProviders] = useState<AidProvider[]>([]);

  const showResponse =
    isAuthenticated && Boolean(wildfire) && wildfireStateLabel(wildfire!) === 'Active';

  useEffect(() => {
    setFireDepartmentEnroute(false);
    setFpaResourcesEnroute(false);
    setAidKeys([]);
  }, [open, wildfire?.id]);

  // Deferred until the sheet is actually opened on an active fire — the map
  // drawer mounts for every marker tap, and most of those never share.
  useEffect(() => {
    if (!open || !showResponse) return;
    let cancelled = false;
    loadAidProviders(tenantId).then((list) => {
      // Fire departments are dropped here rather than in the picker so the
      // selection and the names derived from it can never include one: the
      // "Fire Department enroute" toggle above is where they are declared,
      // and offering both would let the same brigade be announced twice.
      if (!cancelled) {
        setProviders(list.filter((p) => p.org_type !== 'fire_department'));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, showResponse, tenantId]);

  // Named in directory order rather than click order, so two people sharing
  // the same fire produce the same message.
  const aidNames = useMemo(
    () => providers.filter((p) => aidKeys.includes(p.key)).map((p) => p.name),
    [providers, aidKeys],
  );

  const payload = wildfire
    ? buildWildfireSharePayload(wildfire, formatDateTime, {
        // Guarded as well as hidden: a fire that goes inactive while the
        // sheet is open must not keep sending an "Enroute" row.
        fireDepartmentEnroute: showResponse && fireDepartmentEnroute,
        fpaResourcesEnroute: showResponse && fpaResourcesEnroute,
        mutualAidEnroute: showResponse ? aidNames : [],
      })
    : null;

  const toggleAid = (key: string) =>
    setAidKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const controls = showResponse ? (
    <div className="space-y-2.5 rounded-md border border-border bg-muted/30 px-3 py-2.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Response
      </p>
      <ToggleRow
        id="share-fire-dept-enroute"
        label="Fire Department enroute"
        checked={fireDepartmentEnroute}
        onChange={setFireDepartmentEnroute}
      />
      <ToggleRow
        id="share-fpa-resources-enroute"
        label="FPA resources enroute"
        checked={fpaResourcesEnroute}
        onChange={setFpaResourcesEnroute}
      />
      <MutualAidSelect
        providers={providers}
        selected={aidKeys}
        names={aidNames}
        onToggle={toggleAid}
      />
    </div>
  ) : null;

  return (
    <ShareSheet open={open} onOpenChange={onOpenChange} payload={payload} controls={controls} />
  );
}

function ToggleRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="text-sm font-normal">
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * Multi-select over the mutual-aid directory: other FPAs first, then the rest
 * of the organisation register (fire departments excluded — see the fetch).
 * Renders nothing while the list is empty (still loading, or it failed)
 * rather than a dropdown that opens onto nothing.
 */
function MutualAidSelect({
  providers,
  selected,
  names,
  onToggle,
}: {
  providers: AidProvider[];
  selected: string[];
  names: string[];
  onToggle: (key: string) => void;
}) {
  const fpas = providers.filter((p) => p.kind === 'fpa');
  const orgs = providers.filter((p) => p.kind === 'org');

  if (providers.length === 0) return null;

  const row = (p: AidProvider) => (
    <label
      key={p.key}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
    >
      <Checkbox checked={selected.includes(p.key)} onCheckedChange={() => onToggle(p.key)} />
      {p.kind === 'fpa' ? (
        <Shield className="h-3.5 w-3.5 shrink-0 opacity-70" />
      ) : (
        <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
      )}
      <span className="text-sm">{p.name}</span>
      {p.org_type && (
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {ORG_TYPE_LABELS[p.org_type] ?? p.org_type}
        </span>
      )}
    </label>
  );

  return (
    <div className="space-y-1 pt-0.5">
      <Label className="text-sm font-normal">Mutual aid enroute</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-between font-normal">
            <span className={cn('truncate', !names.length && 'text-muted-foreground')}>
              {names.length ? names.join(', ') : 'Nobody selected'}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="max-h-64 w-[--radix-popover-trigger-width] overflow-y-auto p-2"
          align="start"
        >
          {fpas.length > 0 && (
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
              Fire Protection Associations
            </div>
          )}
          {fpas.map(row)}
          {orgs.length > 0 && (
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
              Other organisations
            </div>
          )}
          {orgs.map(row)}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default WildfireShareSheet;
